// Zero-credit, browser-side machine translation for anything the reviewed
// cache does not cover yet (article bodies, card titles, dynamic labels).
//
// Runs from the visitor's own browser, so it never touches workspace credits.
// Tiers: Chrome on-device Translator -> Google free web endpoint -> MyMemory. Any failure leaves the English text in place.

const CODE: Record<string, string> = { zh: "zh-CN" };
const code = (lang: string) => CODE[lang] ?? lang;

const clean = (s: string) => s.replace(/\u2014/g, " - ").replace(/\s+/g, " ").trim();

// ---------- Tier 1: Chrome on-device Translator API ----------
type TranslatorLike = { translate: (t: string) => Promise<string> };
const onDevice = new Map<string, Promise<TranslatorLike | null>>();
function getOnDevice(lang: string): Promise<TranslatorLike | null> {
  if (!onDevice.has(lang)) {
    onDevice.set(
      lang,
      (async () => {
        try {
          const T = (globalThis as any).Translator;
          if (!T?.availability || !T?.create) return null;
          const opts = { sourceLanguage: "en", targetLanguage: lang };
          // Some browsers expose the API but never settle - never block on it.
          const within = <V,>(p: Promise<V>) =>
            Promise.race([p, new Promise<null>((r) => setTimeout(() => r(null), 1500))]);
          if ((await within(T.availability(opts))) !== "available") return null;
          const t = (await within(T.create(opts))) as TranslatorLike | null;
          if (!t) return null;
          const probe = await within(t.translate("Hello"));
          return typeof probe === "string" ? t : null;
        } catch {
          return null;
        }
      })(),
    );
  }
  return onDevice.get(lang)!;
}

// ---------- Tier 2: Google Translate free web endpoint (founder approved Oct 2026) ----------
const googleDown = new Set<string>();
function googleChunks(text: string): string[] {
  if (text.length <= 1500) return [text];
  const out: string[] = [];
  let cur = "";
  for (const s of text.split(/(?<=[.!?])\s+/)) {
    if (cur && (cur + " " + s).length > 1500) { out.push(cur); cur = s; }
    else cur = cur ? `${cur} ${s}` : s;
  }
  if (cur) out.push(cur);
  return out;
}
async function googleOne(lang: string, text: string): Promise<string | null> {
  if (googleDown.has(lang)) return null;
  try {
    const parts: string[] = [];
    for (const seg of googleChunks(text)) {
      const res = await fetch(
        `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${encodeURIComponent(code(lang))}&dt=t&q=${encodeURIComponent(seg)}`,
      );
      if (!res.ok) { if (res.status === 429) googleDown.add(lang); return null; }
      const j = (await res.json()) as any;
      const t = Array.isArray(j?.[0]) ? j[0].map((x: any) => x?.[0] ?? "").join("") : "";
      if (!t) return null;
      parts.push(t);
    }
    return clean(parts.join(" "));
  } catch {
    // Browser blocked the request (e.g. CORS) - stop trying for this visit.
    googleDown.add(lang);
    return null;
  }
}

// ---------- Tier 3: MyMemory (request-safe segments) ----------
const memoryExhausted = new Set<string>();
function sentences(text: string): string[] {
  // The endpoint limits the encoded query to 500 bytes, not 500 characters.
  // Keep a conservative Unicode-safe ceiling so Arabic/Hindi/CJK requests do
  // not fail after an English-sized segment is translated.
  const fits = (value: string) => new TextEncoder().encode(value).length <= 360;
  if (fits(text)) return [text];
  const out: string[] = [];
  let cur = "";
  for (const s of text.split(/(?<=[.!?])\s+/)) {
    if (!fits(cur ? `${cur} ${s}` : s) && cur) {
      out.push(cur);
      cur = s;
    } else cur = cur ? `${cur} ${s}` : s;
  }
  if (cur) out.push(cur);
  return out.flatMap((part) => {
    if (fits(part)) return [part];
    const chunks: string[] = [];
    let chunk = "";
    for (const word of part.split(/\s+/)) {
      const candidate = chunk ? `${chunk} ${word}` : word;
      if (!fits(candidate) && chunk) {
        chunks.push(chunk);
        chunk = word;
      } else chunk = candidate;
    }
    if (chunk) chunks.push(chunk);
    return chunks;
  });
}
async function memoryOne(lang: string, text: string): Promise<string | null> {
  if (memoryExhausted.has(lang)) return null;
  try {
    const parts: string[] = [];
    for (const seg of sentences(text)) {
      const res = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodeURIComponent(seg)}&langpair=en|${encodeURIComponent(code(lang))}&de=support%40smartasaurus.com`,
      );
      if (!res.ok) return null;
      const j = (await res.json()) as any;
      if (j?.quotaFinished) {
        memoryExhausted.add(lang);
        return null;
      }
      const t = j?.responseData?.translatedText;
      if (j?.responseStatus !== 200 || typeof t !== "string" || /MYMEMORY WARNING|QUERY LENGTH/i.test(t)) return null;
      parts.push(t);
    }
    return clean(parts.join(" "));
  } catch {
    return null;
  }
}

/** Translate English strings; returns source -> translation for successes only. */
export async function freeTranslate(
  lang: string,
  sources: string[],
  options: { sourceIsEnglish?: boolean } = {},
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  // Text that already carries accented/non-Latin letters is almost always an
  // approved translation on the page - never machine-translate it again.
  const looksTranslated = /[\u00C0-\u024F\u0400-\u04FF\u0600-\u06FF\u0900-\u097F\u3040-\u30FF\u4E00-\u9FFF]/;
  const todo = Array.from(
    new Set(sources.map(clean).filter((t) => t && (options.sourceIsEnglish || !looksTranslated.test(t)))),
  );
  if (lang === "en" || todo.length === 0) return out;

  const device = await getOnDevice(lang);
  if (device) {
    for (const t of todo) {
      try {
        const r = clean((await Promise.race([device.translate(t), new Promise<string>((res) => setTimeout(() => res(""), 4000))])) || "");
        if (r) out.set(t, r);
      } catch {
        /* fall through to the web tiers */
      }
    }
  }

  let left = todo.filter((t) => !out.has(t));
  {
    let gi = 0;
    await Promise.all(
      Array.from({ length: Math.min(6, left.length) }, async () => {
        while (gi < left.length && !googleDown.has(lang)) {
          const t = left[gi++]!;
          const r = await googleOne(lang, t);
          if (r) out.set(t, r);
        }
      }),
    );
  }
  left = todo.filter((t) => !out.has(t));
  // Four at a time keeps it quick without flooding the free service.
  let next = 0;
  await Promise.all(
    Array.from({ length: Math.min(4, left.length) }, async () => {
      while (next < left.length && !memoryExhausted.has(lang)) {
        const t = left[next++]!;
        const r = await memoryOne(lang, t);
        if (r) out.set(t, r);
      }
    }),
  );
  return out;
}

export const normalizeSource = clean;
